/** Category / taxonomy normalization for catalog API, JSON file, and static data. */

import { coerceMediaList } from './productMediaUtils';

/** Treat missing / unknown as visible (shop-safe default). */
export function coerceCatalogVisible(v) {
  if (v === false || v === 0 || v === '0') return false;
  return true;
}

/** Category / subcategory / product type / product return policy (matches PHP). */
export function coerceReturnAllowance(v) {
  const s = String(v ?? 'inherit').trim().toLowerCase();
  if (s === 'returnable' || s === 'non_returnable') return s;
  return 'inherit';
}

/** PHP json_encode may emit categories as a JSON object; empty product categories must still appear. */
export function coalesceCategoriesInput(raw) {
  if (raw == null) return [];
  if (Array.isArray(raw)) return raw.filter((c) => c && typeof c === 'object');
  if (typeof raw === 'object') return Object.values(raw).filter((c) => c && typeof c === 'object');
  return [];
}

/** Canonical lowercase slug keys so API + editor stay aligned with MySQL. */
export function normalizeProductSlugKeys(productsObj) {
  const src = productsObj && typeof productsObj === 'object' && !Array.isArray(productsObj) ? productsObj : {};
  const out = {};
  for (const [k, v] of Object.entries(src)) {
    const key = String(k).trim().toLowerCase();
    if (!key) continue;
    out[key] = Array.isArray(v) ? v : [];
  }
  return out;
}

/**
 * Union of categories from the API/file and every product bucket (includes empty categories).
 */
export function mergeTaxonomyFromApi(apiCategories, productsObj) {
  const products = normalizeProductSlugKeys(productsObj);
  const bySlug = new Map();
  coalesceCategoriesInput(apiCategories).forEach((c, i) => {
    const slug = String(c.slug ?? '').trim().toLowerCase();
    if (!slug) return;
    bySlug.set(slug, {
      ...c,
      slug,
      subcategories: c.subcategories || [],
      types: c.types || [],
      sort_order: typeof c.sort_order === 'number' ? c.sort_order : i,
      is_visible: coerceCatalogVisible(c.is_visible),
      return_allowance: coerceReturnAllowance(c.return_allowance),
    });
  });
  Object.keys(products).forEach((slug) => {
    if (bySlug.has(slug)) return;
    bySlug.set(slug, {
      slug,
      name: slug.charAt(0).toUpperCase() + slug.slice(1),
      sort_order: bySlug.size,
      hero_tagline: '',
      hero_blurb: '',
      cover_image: '',
      is_visible: true,
      subcategories: [],
      types: [],
      return_allowance: 'inherit',
    });
  });
  return [...bySlug.values()].sort((a, b) => (a.sort_order ?? 0) - (b.sort_order ?? 0) || a.slug.localeCompare(b.slug));
}

export function deriveCategoriesFromProducts(productsObj) {
  const keys = [
    ...new Set(
      Object.keys(productsObj || {})
        .map((k) => String(k).trim().toLowerCase())
        .filter((k) => k !== '')
    ),
  ];
  return keys.map((slug, i) => ({
    slug,
    name: slug.charAt(0).toUpperCase() + slug.slice(1),
    sort_order: i,
    hero_tagline: '',
    hero_blurb: '',
    cover_image: '',
    is_visible: true,
    return_allowance: 'inherit',
  }));
}

export function normalizeSubcategories(subs) {
  if (!Array.isArray(subs)) return [];
  return subs
    .map((s, j) => ({
      slug: String(s.slug || '').trim().toLowerCase(),
      name: String(s.name || s.slug || '').trim() || String(s.slug || '').trim(),
      sort_order: typeof s.sort_order === 'number' ? s.sort_order : j,
      is_visible: coerceCatalogVisible(s.is_visible),
      return_allowance: coerceReturnAllowance(s.return_allowance),
      cover_image: String(s.cover_image ?? '').trim(),
    }))
    .filter((s) => s.slug);
}

export function normalizeTypes(types) {
  if (!Array.isArray(types)) return [];
  return types
    .map((t, j) => ({
      slug: String(t.slug || '').trim(),
      name: String(t.name || t.slug || '').trim() || String(t.slug || '').trim(),
      sort_order: typeof t.sort_order === 'number' ? t.sort_order : j,
      is_visible: coerceCatalogVisible(t.is_visible),
      return_allowance: coerceReturnAllowance(t.return_allowance),
      cover_image: String(t.cover_image ?? '').trim(),
    }))
    .filter((t) => t.slug);
}

export function inferSubcategoriesFromProducts(productList) {
  const seen = new Map();
  (productList || []).forEach((p) => {
    const sc = String(p.subcategory || '').trim();
    if (sc) seen.set(sc, seen.size);
  });
  return [...seen.keys()]
    .sort()
    .map((s, i) => ({ slug: s, name: s, sort_order: i, is_visible: true, return_allowance: 'inherit', cover_image: '' }));
}

export function inferTypesFromProducts(productList) {
  const seen = new Map();
  (productList || []).forEach((p) => {
    const t = String(p.type || '').trim();
    if (t) seen.set(t, seen.size);
  });
  return [...seen.keys()]
    .sort()
    .map((s, i) => ({ slug: s, name: s, sort_order: i, is_visible: true, return_allowance: 'inherit', cover_image: '' }));
}

export function normalizeCategoryList(cats, productsObj) {
  const list =
    cats && cats.length > 0
      ? cats
      : deriveCategoriesFromProducts(productsObj && typeof productsObj === 'object' ? productsObj : {});
  return list
    .map((c, i) => {
      if (!c || typeof c !== 'object') return null;
      const slug = String(c.slug ?? '').trim().toLowerCase();
      if (!slug) return null;
      let subs = normalizeSubcategories(c.subcategories);
      let types = normalizeTypes(c.types);
      const plist = productsObj && productsObj[slug];
      if (subs.length === 0 && Array.isArray(plist)) {
        subs = inferSubcategoriesFromProducts(plist);
      }
      if (types.length === 0 && Array.isArray(plist)) {
        types = inferTypesFromProducts(plist);
      }
      return {
        slug,
        name: c.name || slug,
        sort_order: typeof c.sort_order === 'number' ? c.sort_order : i,
      hero_tagline: String(c.hero_tagline ?? '').trim(),
      hero_blurb: String(c.hero_blurb ?? '').trim(),
      cover_image: String(c.cover_image ?? '').trim(),
      is_visible: coerceCatalogVisible(c.is_visible),
        return_allowance: coerceReturnAllowance(c.return_allowance),
        subcategories: subs,
        types,
      };
    })
    .filter(Boolean);
}

export function buildAllProducts(productsObj) {
  return Object.entries(productsObj || {}).flatMap(([slug, list]) =>
    Array.isArray(list) ? list.map((p) => ({ ...p, category: slug })) : []
  );
}

/** Lowercase slug for category / product bucket keys. */
export function normalizeCategorySlug(slug) {
  return String(slug ?? '').trim().toLowerCase();
}

/**
 * Resolve category row for the product editor (case-insensitive).
 * Falls back to a product bucket when taxonomy list is out of sync.
 */
export function resolveCategoryMeta(categories, productsByCategory, slug) {
  const key = normalizeCategorySlug(slug);
  if (!key) return null;

  const products =
    productsByCategory && typeof productsByCategory === 'object' && !Array.isArray(productsByCategory)
      ? productsByCategory
      : {};
  const plist = Array.isArray(products[key]) ? products[key] : [];

  const list = Array.isArray(categories) ? categories : [];
  const found = list.find((c) => normalizeCategorySlug(c?.slug) === key);
  if (found) {
    let subs = normalizeSubcategories(found.subcategories);
    let types = normalizeTypes(found.types);
    if (subs.length === 0 && plist.length > 0) {
      subs = inferSubcategoriesFromProducts(plist);
    }
    if (types.length === 0 && plist.length > 0) {
      types = inferTypesFromProducts(plist);
    }
    return {
      ...found,
      slug: key,
      subcategories: subs,
      types,
    };
  }

  if (!Object.prototype.hasOwnProperty.call(products, key)) {
    return null;
  }

  let subs = [];
  let types = [];
  if (plist.length > 0) {
    subs = inferSubcategoriesFromProducts(plist);
    types = inferTypesFromProducts(plist);
  }

  return {
    slug: key,
    name: key.charAt(0).toUpperCase() + key.slice(1).replace(/-/g, ' '),
    sort_order: list.length,
    hero_tagline: '',
    hero_blurb: '',
    cover_image: '',
    is_visible: true,
    return_allowance: 'inherit',
    subcategories: subs,
    types,
  };
}
